import { stagesConfig } from "@/components/job-postings/KanbanStageConfig"
import { ukDayStart } from "@/constants/sheriffOptions"
import { supabase } from "@/lib/supabase"
import { ukFormat } from "@/lib/ukFormat"
import { applicationFilterEmbeds, applyApplicationFilters } from "@/services/applicationFilters"
import { FilterState } from "@/types/application"

export type ApplicationExportRow = {
  applicationId: string
  name: string
  location: string
  stage: string
  comments: string
  email: string
  phone: string
}

type One<T> = T | T[] | null

type CommentRow = {
  comment: string | null
  created_at: string | null
  user: One<{ full_name: string | null }>
}

export type ApplicationExportRecord = {
  application_id: string | null
  current_stage: string | null
  applicant: One<{
    first_name: string | null
    last_name: string | null
    email: string | null
    phone_number: string | null
  }>
  job: One<{ depot: One<{ name: string | null }> }>
  comments: CommentRow[] | null
}

const STAGE_LABELS: Record<string, string> = Object.fromEntries(
  stagesConfig.map((s) => [s.stage, s.label]),
)

const one = <T>(value: One<T>): T | null => (Array.isArray(value) ? value[0] : value) ?? null

const firstOfMonth = (year: number, monthIndex: number) =>
  new Date(Date.UTC(year, monthIndex, 1)).toISOString().slice(0, 10)

export const quarterRange = (quarter: string) => {
  const [year, q] = quarter.split("-Q").map(Number)
  const startMonth = (q - 1) * 3
  return {
    start: ukDayStart(firstOfMonth(year, startMonth)),
    end: ukDayStart(firstOfMonth(year, startMonth + 3)),
  }
}

export const toApplicationExportRow = (app: ApplicationExportRecord): ApplicationExportRow => {
  const applicant = one(app.applicant)
  const depot = one(one(app.job)?.depot ?? null)
  const comments = [...(app.comments || [])]
    .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""))
    .map((c) => {
      const date = c.created_at ? ukFormat(c.created_at, "dd/MM/yyyy") : ""
      return `${date} ${one(c.user)?.full_name || "Admin"}: ${c.comment ?? ""}`.trim()
    })
    .join("\n")

  return {
    applicationId: app.application_id || "",
    name: [applicant?.first_name, applicant?.last_name].filter(Boolean).join(" "),
    location: depot?.name || "",
    stage: STAGE_LABELS[app.current_stage ?? ""] ?? app.current_stage ?? "",
    comments,
    email: applicant?.email || "",
    phone: applicant?.phone_number || "",
  }
}

export async function fetchApplicationsForExport({
  quarter,
  searchQuery = "",
  filters = {},
}: {
  quarter: string
  searchQuery?: string
  filters?: Partial<FilterState>
}): Promise<ApplicationExportRow[]> {
  const { start, end } = quarterRange(quarter)
  const all: ApplicationExportRow[] = []
  const pageSize = 1000
  let from = 0

  const selectColumns = `
    application_id,
    current_stage,
    applicant:applicant_id(first_name, last_name, email, phone_number),
    job:job_id(depot:depot_id(name)),
    comments(comment, created_at, user:user_id(full_name)),
    ${applicationFilterEmbeds(filters, searchQuery)}
    id
  `

  while (true) {
    let query = supabase
      .from("applications")
      .select(selectColumns)
      .gte("created_at", start.toISOString())
      .lt("created_at", end.toISOString())
      .order("changed_stage_at", { ascending: false, nullsFirst: false })
      .order("id")

    query = applyApplicationFilters(query, filters, searchQuery)

    const { data, error } = await query.range(from, from + pageSize - 1)
    if (error) throw error

    const rows = (data || []) as unknown as ApplicationExportRecord[]
    all.push(...rows.map(toApplicationExportRow))

    if (rows.length < pageSize) break
    from += pageSize
  }

  return all
}
